import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid } from "components/DataGrid/DataGrid";
import { POHeader } from "components/POHeader/POHeader";
import {
  PAGE,
  PO_ELEMENTS,
  PO_ITEM_STATUS,
  PO_REASON,
  PO_STATUS,
  USER_ROLE,
} from "components/common/CONSTANTS";
import Typography from "@mui/material/Typography";
import { CommonHeader } from "../common/CommonHeader";
import { createInputColumnDefs } from "components/DataGrid/columnDefinitions";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { SelectReasonDialog } from "components/common/SelectReasonDialog";
import { useSelector, useDispatch } from "react-redux";
import { createPO } from "redux/actions/createPOAction";
import {
  handleCasesEdit,
  handlePalletsEdit,
  calculateTotalCases,
  calculateTotalBases,
  calculateTotalPallets,
  calculateTotalWagons,
} from "components/DataGrid/utils";
import {
  PO_SEARCH_OPPORTUNITIES_RESET,
  RESET_CREATE_PURCHASE_ORDER,
} from "redux/actions/action-type";
import dayjs from "dayjs";
import { showNotificationDialog } from "redux/actions/notificationActions";
import { showErrorMessageByCode } from "services/commonServices";

export const CreateManualPOTable = () => {
  const userType = useSelector((state) => state.user);
  const { accessiblePages, userDetailResponse } = userType.userType;
  const { rolePrivilegeDetails } = userDetailResponse;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [handball, setHandball] = useState();
  const [rounding, setRounding] = useState();
  const [overseas, setOverseas] = useState();
  const [columnDefs, setColumnDefs] = useState();
  const [reasonDialog, setReasonDialog] = useState(false);
  const [itemsData, setItemsData] = useState();
  const [headerData, setHeaderData] = useState();
  const [orderDate, setOrderDate] = useState();
  const [deliveryDate, setDeliveryDate] = useState();
  const [comments, setComments] = useState();
  const [accessibleButtonlist, setAccessibleButtonlist] = useState([]);
  const [totalCases, setTotalCases] = useState();
  const [totalPallets, setTotalPallets] = useState();
  const [totalBases, setTotalBases] = useState();
  const [totalWagons, setTotalWagons] = useState();
  const [isSaved, setIsSaved] = useState(false);
  const [payload, setPayload] = useState(null);

  const {
    isCreatePOLoading,
    isCreatePOSuccess,
    isCreatePOError,
    errorDetails,
  } = useSelector((state) => state.createPO);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { searchOrderOpportunitiesData } = useSelector(
    (state) => state.poSearchOpportunities
  );

  const { noOfOrders, categorys } = location.state;

  useEffect(() => {
    return () => {
      dispatch({ type: PO_SEARCH_OPPORTUNITIES_RESET });
    };
  }, [dispatch]);

  useEffect(() => {
    setAccessibleButtonlist(accessiblePages[PAGE.CREATE_MANUAL_ORDER_PO.key]);
  }, [accessiblePages]);

  useEffect(() => {
    if (isCreatePOSuccess) {
      setDialogOpen(true);
    }
  }, [isCreatePOSuccess]);

  useEffect(() => {
    const handlePageShow = (event) => {
      const navigatedFromCreate = sessionStorage.getItem("navigatedFromCreate");
      if (
        event.persisted ||
        performance.getEntriesByType("navigation")[0]?.type === "reload"
      ) {
        if (navigatedFromCreate) {
          navigate(PAGE.UPLOAD_CREATE_MANUAL_ORDER_PO.url, { replace: true });
        }
      }
      sessionStorage.removeItem("navigatedFromCreate");
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [navigate]);

  useEffect(() => {
    setHandball(rolePrivilegeDetails[0].roleName === USER_ROLE.CENTRAL_IMPORTS);
    setOverseas(rolePrivilegeDetails[0].roleName === USER_ROLE.CENTRAL_IMPORTS);
  }, [rolePrivilegeDetails]);

  useEffect(() => {
    if (
      searchOrderOpportunitiesData &&
      Object.keys(searchOrderOpportunitiesData).length > 0
    ) {
      const { items } = searchOrderOpportunitiesData;
      const {
        isRoundingOn,
        deliveryMethod,
        palletPerWagon,
        orderDate,
        deliveryDate,
      } = items[0];

      setRounding(isRoundingOn);
      const orderDates = {};
      const deliveryDates = {};
      for (let index = 0; index < noOfOrders; index++) {
        orderDates[index] = dayjs(orderDate).format("YYYY-MM-DDTHH:mm:ss");
        deliveryDates[index] = deliveryDate
          ? dayjs(deliveryDate).format("YYYY-MM-DDTHH:mm:ss")
          : null;
      }
      setOrderDate(orderDates);
      setDeliveryDate(deliveryDates);

      setHeaderData({
        supplierId: `${items[0].supplierId}`,
        supplierName: `${items[0].supplierName}`,
        shipToLocationId: `${items[0].shipToLocationId}`,
        shipToLocationName: `${items[0].shipToLocationName}`,
        category: categorys,
        deliveryMethod: deliveryMethod,
        palletPerWagon: palletPerWagon,
      });

      const itemList = [];

      items.forEach((item) => {
        const updatedItem = { ...item };
        if (!item.isDoNotOrderProduct) {
          for (let i = 0; i < noOfOrders; i++) {
            if (item.quantityInCases && item.quantityInCases > 0) {
              updatedItem[`po${i + 1}Cases`] = item.quantityInCases;
              updatedItem[`po${i + 1}CasesRounded`] = item.quantityInCases;
            }

            if (item.quantityInPallets && item.quantityInPallets > 0) {
              updatedItem[`po${i + 1}PalletsRounded`] = item.quantityInPallets;
              updatedItem[`po${i + 1}Pallets`] = item.quantityInPallets;
            }
          }
        }
        itemList.push(updatedItem);
      });
      setItemsData(itemList);
    }
  }, [searchOrderOpportunitiesData, categorys, noOfOrders]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const updateColumns = () => {
      const isMobile = window.innerWidth < 960;

      if (noOfOrders) {
        const dynamicColumns = Array.from({ length: noOfOrders }, (_, i) => ({
          headerName: `PO-${i + 1}`,
          children: [
            {
              headerName: "Order Qty (Cases)",
              field: rounding ? `po${i + 1}CasesRounded` : `po${i + 1}Cases`,
              editable: (params) => !params.data?.isDoNotOrderProduct,
              width: 80,
              minWidth: 80,
              flex: 1,
              filter: false,
              headerClass: "centered-header",
              cellStyle: { textAlign: "right" },
              valueSetter: (params) =>
                handleCasesEdit(params, `po${i + 1}`, setItemsData),
              cellEditor: "agNumberCellEditor",
            },
            {
              headerName: "Order Qty (Pallets)",
              field: rounding
                ? `po${i + 1}PalletsRounded`
                : `po${i + 1}Pallets`,
              editable: (params) => !params.data?.isDoNotOrderProduct,
              minWidth: 80,
              flex: 1,
              filter: false,
              headerClass: "centered-header",
              cellStyle: { textAlign: "right" },
              valueSetter: (params) =>
                handlePalletsEdit(params, `po${i + 1}`, setItemsData),
              cellEditor: "agNumberCellEditor",
            },
          ],
        }));

        setColumnDefs([...createInputColumnDefs(isMobile), ...dynamicColumns]);
      }
    };

    updateColumns();

    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, [noOfOrders, rounding]);
  const handleHandballToggle = () => {
    setHandball(!handball);
  };

  const handleRoundingToggle = () => {
    setRounding(!rounding);
  };

  const handleOverseasToggle = () => {
    setOverseas(!overseas);
  };

  const onDialogBackButtonClicked = () => {
    setIsSaved(false);
    setPayload(null);
    setReasonDialog(false);
  };

  useEffect(() => {
    if (itemsData) {
      for (let i = 0; i <= noOfOrders; i++) {
        calculateTotalCases(i, itemsData, setTotalCases, rounding);
        calculateTotalPallets(i, itemsData, setTotalPallets, rounding);
        calculateTotalBases(i, itemsData, setTotalBases, rounding);
        calculateTotalWagons(
          i,
          itemsData,
          setTotalWagons,
          itemsData[0].palletPerWagon,
          rounding
        );
      }
    }
  }, [itemsData, noOfOrders, rounding]);

  useEffect(() => {
    if (isCreatePOError && errorDetails) {
      dispatch({ type: RESET_CREATE_PURCHASE_ORDER });
      const { errTitle, errContent } = showErrorMessageByCode(errorDetails);
      dispatch(showNotificationDialog(errTitle, errContent, "Ok"));
    }
  }, [isCreatePOError, dispatch, errorDetails]);

  const onConfirmReasonClicked = (reason) => {
    setReasonDialog(false);
    const payloadArray = payload.map((item) => {
      return {
        ...item,
        reasonCode: reason,
      };
    });
    // setIsSaved(false);
    dispatch(createPO({ payloadArray: payloadArray }));
  };

  const handleDialogClose = () => {
    setPayload(null);
    setIsSaved(false);
    setDialogOpen(false);
    dispatch({ type: RESET_CREATE_PURCHASE_ORDER });
    navigate(PAGE.UPLOAD_CREATE_MANUAL_ORDER_PO.url);
  };

  const areAllDatesValid = () => {
    for (let i = 0; i < noOfOrders; i++) {
      if (!orderDate[i] || !deliveryDate[i]) {
        return false;
      }
      const orderDateObj = dayjs(orderDate[i]).startOf("day");
      const deliveryDateObj = dayjs(deliveryDate[i]).startOf("day");

      if (orderDateObj.isAfter(deliveryDateObj)) {
        return false;
      }
    }
    return true;
  };

  const handleSave = () => {
    if (!areAllDatesValid()) {
      dispatch(
        showNotificationDialog(
          "Invalid Dates",
          "Please enter valid order & delivery dates and try again",
          "Ok"
        )
      );
      return;
    }

    const payloadArray = [];
    for (let i = 0; i < noOfOrders; i++) {
      const caseKey = rounding ? `po${i + 1}CasesRounded` : `po${i + 1}Cases`;
      const palletKey = rounding
        ? `po${i + 1}PalletsRounded`
        : `po${i + 1}Pallets`;

      let updatedItems = itemsData.filter((item) => {
        return item[caseKey] !== undefined && item[caseKey] !== null;
      });

      if (updatedItems.length === 0) continue;

      updatedItems = itemsData.filter((item) => {
        return !item.isDoNotOrderProduct
      }).map((item) => {
        const { [caseKey]: cases, [palletKey]: pallets, ...rest } = item;
        return {
          ...rest,
          quantityOrdered: cases,
          palletQuantity: pallets,
          status: PO_ITEM_STATUS.DRAFT,
        };
      });

      payloadArray.push({
        ...headerData,
        orderDate: orderDate[i],
        deliveryDate: deliveryDate[i],
        userComments: comments ? comments[i] : "",
        palletPerWagon: itemsData[0].palletPerWagon,
        supplierSiteId: itemsData[0].supplierSiteId,
        supplierId: itemsData[0].supplierId,
        shipToLocationId: itemsData[0].shipToLocationId,
        isHandball: handball,
        isOverseas: overseas,
        isQuantityRounded: rounding,
        items: updatedItems,
        totalCasesCount: totalCases[i],
        totalPalletCount: totalPallets[i],
        totalBasesCount: totalBases[i],
        totalWagonsCount: totalWagons[i],
      });
    }

    const hasValidQuantities = payloadArray.some((payload) =>
      payload.items.some(
        (item) =>
          (item.quantityOrdered &&
            item.quantityOrdered !== 0 &&
            item.quantityOrdered !== null &&
            item.quantityOrdered !== "") ||
          (item.palletQuantity &&
            item.palletQuantity !== 0 &&
            item.palletQuantity !== null &&
            item.palletQuantity !== "")
      )
    );

    if (!hasValidQuantities) {
      dispatch(
        showNotificationDialog(
          "No Quantities Entered",
          "Please enter quantity for at least 1 item in a PO",
          "Ok"
        )
      );
      return;
    }

    setIsSaved(true);
    setPayload(payloadArray);
    setReasonDialog(true);
  };

  const handleConfirm = () => {
    if (!areAllDatesValid()) {
      dispatch(
        showNotificationDialog(
          "Invalid Dates",
          "Please enter valid order & delivery dates and try again",
          "Ok"
        )
      );
      return;
    }

    const payloadArray = [];
    for (let i = 0; i < noOfOrders; i++) {
      const caseKey = rounding ? `po${i + 1}CasesRounded` : `po${i + 1}Cases`;
      const palletKey = rounding
        ? `po${i + 1}PalletsRounded`
        : `po${i + 1}Pallets`;

      const updatedItems = itemsData
        .filter((item) => {
          return item[caseKey] !== undefined && item[caseKey] !== null;
        })
        .map((item) => {
          const { [caseKey]: cases, [palletKey]: pallets, ...rest } = item;
          return {
            ...rest,
            quantityOrdered: cases,
            palletQuantity: pallets,
            status: PO_ITEM_STATUS.CREATE,
            category: item.category,
          };
        });

      if (updatedItems.length === 0) continue;

      payloadArray.push({
        ...headerData,
        orderDate: orderDate[i],
        deliveryDate: deliveryDate[i],
        userComments: comments ? comments[i] : "",
        palletPerWagon: itemsData[0].palletPerWagon,
        supplierSiteId: itemsData[0].supplierSiteId,
        supplierId: itemsData[0].supplierId,
        shipToLocationId: itemsData[0].shipToLocationId,
        isHandball: handball,
        isOverseas: overseas,
        isQuantityRounded: rounding,
        items: updatedItems,
        status: PO_STATUS.CONFIRMED,
        totalCasesCount: totalCases[i],
        totalPalletCount: totalPallets[i],
        totalBasesCount: totalBases[i],
        totalWagonsCount: totalWagons[i],
      });
    }

    if (payloadArray.length === 0) {
      dispatch(
        showNotificationDialog(
          "No Quantities Entered",
          "Please enter quantity for at least 1 item in a PO",
          "Ok"
        )
      );
      return;
    }

    setPayload(payloadArray);
    setReasonDialog(true);
  };

  const handleBackPress = () => {
    dispatch({ type: PO_SEARCH_OPPORTUNITIES_RESET });
    navigate(PAGE.UPLOAD_CREATE_MANUAL_ORDER_PO.url);
  };

  const getRowStyle = params => {
    if (params.data.isDoNotOrderProduct) {
        return { backgroundColor: '#f0f0f0', color: '#999' };
    }
    return null;
  };

  return (
    <>
      <div className="mainPageStyling">
        {isCreatePOLoading && (
          <Backdrop
            sx={{
              color: "#fff",
              flexDirection: "column",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={true}
          >
            <CircularProgress color="inherit" />

            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Loading...
            </Typography>
          </Backdrop>
        )}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 2 }}>
            {PAGE.UPLOAD_CREATE_MANUAL_ORDER_PO.label}
          </Typography>
        </div>
        <div style={{ marginBottom: "10px" }} className="labelControl">
          {headerData && (
            <CommonHeader
              data={headerData}
              handball={handball}
              handleHandballToggle={handleHandballToggle}
              rounding={rounding}
              handleRoundingToggle={handleRoundingToggle}
              overseas={overseas}
              handleOverseasToggle={handleOverseasToggle}
            />
          )}
        </div>
        {itemsData && (
          <>
            <POHeader
              data={itemsData}
              rounding={rounding}
              noOfOrders={noOfOrders}
              comments={comments}
              setComments={setComments}
              orderDate={orderDate}
              setOrderDate={setOrderDate}
              deliveryDate={deliveryDate}
              setDeliveryDate={setDeliveryDate}
              totalCases={totalCases}
              totalPallets={totalPallets}
              totalBases={totalBases}
              totalWagons={totalWagons}
            />
            <div style={{ width: "100%", height: "calc( 100vh - 335px)" }}>
              <DataGrid
                rowData={itemsData}
                columnDefs={columnDefs}
                heightOffset={350}
                getRowStyle={getRowStyle}
                defaultColDef={{
                  flex: 1,
                  sortable: false,
                  filter: true,
                  resizable: false,
                  wrapHeaderText: true,
                  autoHeaderHeight: true,
                  headerClass: "header-wrap",
                }}
              />
            </div>
          </>
        )}

        <div className="parentPoButtons">
          {accessibleButtonlist.includes(PO_ELEMENTS.CREATE_PO.BACK_BUTTON) && (
            <Button
              className="commonPOButtons"
              variant="contained"
              color="secondary"
              onClick={handleBackPress}
              style={{
                marginRight: "8px",
              }}
            >
              Back
            </Button>
          )}
          {accessibleButtonlist.includes(PO_ELEMENTS.CREATE_PO.SAVE_BUTTON) && (
            <Button
              className="commonPOButtons"
              variant="contained"
              color="primary"
              onClick={handleSave}
              style={{
                marginRight: "8px",
              }}
            >
              Save
            </Button>
          )}
          {accessibleButtonlist.includes(
            PO_ELEMENTS.CREATE_PO.CONFIRM_BUTTON
          ) && (
            <Button
              className="commonPOButtons"
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              style={{
                marginRight: "8px",
              }}
            >
              Confirm
            </Button>
          )}
        </div>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <div className="dialogStyling">
            <DialogTitle>
              {!isSaved && <>PO Confirmation</>}
              {isSaved && <>PO Saved</>}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {!isSaved && <>POs have been successfully confirmed.</>}
                {isSaved && <>POs have been successfully saved.</>}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                className="commonPOButtons"
                variant="contained"
                color="primary"
                onClick={handleDialogClose}
              >
                OK
              </Button>
            </DialogActions>
          </div>
        </Dialog>

        <SelectReasonDialog
          reasonType={PO_REASON.ORDER_SUB_TYPE.CREATE}
          title={"Select Manual Purchase Order Reason"}
          message={"Please select manual purchase order creation reason"}
          selectReasonDialog={reasonDialog}
          onReasonDialogClose={onDialogBackButtonClicked}
          handleConfirmReason={onConfirmReasonClicked}
        />
      </div>
    </>
  );
};
